import { InjectionToken, Provider } from '@angular/core';

/** Tab bar the shell renders: the underlined default, the segmented (pill) variant, or none. */
export type PredictionGameTabBarVariant = 'default' | 'segmented' | 'none';

/**
 * How the shared prediction game renders in the host it is embedded in.
 *
 * Distinct from `PREDICTION_GAME_FEATURE_CONFIG` (what a tenant's game consists of): this
 * is presentation the host may change per launch, e.g. when the game runs as an iFrame
 * inside a club app's matchday tab that brings its own navigation and refresh handling.
 * The app layer derives it (URL params, build config) and provides it; without a
 * provider the full standalone presentation applies.
 */
export interface PredictionGameDisplayConfig {
  /** Which tab bar the shell renders: the underlined default, the segmented (pill) variant, or none. */
  readonly tabBar: PredictionGameTabBarVariant;
  /** Whether the shell wraps its content in pull-to-refresh. */
  readonly pullToRefresh: boolean;
  /** Whether the predict tab shows the match paginator (match header, arrows, countdown). */
  readonly matchPaginator: boolean;
  /** When true, the fixture requested via MATCH_CONTEXT.heimspielId MUST exist; otherwise the facade reports it as missing instead of silently falling back to the default selection. */
  readonly requirePinnedFixture: boolean;
  /**
   * Whether failed background loads of the match data (squad, scoring config) are reported
   * via snackbar. A host that renders a full-view error state turns it off.
   */
  readonly loadErrorSnackbar: boolean;
}

export const DEFAULT_PREDICTION_GAME_DISPLAY_CONFIG: PredictionGameDisplayConfig = {
  tabBar: 'default',
  pullToRefresh: true,
  matchPaginator: true,
  requirePinnedFixture: false,
  loadErrorSnackbar: true,
};

export const PREDICTION_GAME_DISPLAY_CONFIG = new InjectionToken<PredictionGameDisplayConfig>(
  'PREDICTION_GAME_DISPLAY_CONFIG',
  { providedIn: 'root', factory: () => DEFAULT_PREDICTION_GAME_DISPLAY_CONFIG },
);

/** Provides the display configuration; fields left out keep their default. */
export function providePredictionGameDisplayConfig(config: Partial<PredictionGameDisplayConfig>): Provider {
  return {
    provide: PREDICTION_GAME_DISPLAY_CONFIG,
    useValue: { ...DEFAULT_PREDICTION_GAME_DISPLAY_CONFIG, ...config },
  };
}
